import {
  FALLBACK_DASH,
  FALLBACK_ELLIPSIS,
  ROUTE_STATUS_EXPORT_CSV_TABLE_COLUMNS,
} from "./Constants";
import { convertMillisecondsToHHMM } from "../../util/Util";
import {
  getPicklistProgress,
  getPicklistLocation,
  renderRouteSpeed,
  getPicklistStageTime,
} from "./Util";

/**
 * Converts route status data into CSV format for export.
 * Creates a 2D array with headers and route/picklist data rows.
 *
 * @param {Array} routeList - Array of route objects to convert
 * @param {string} routeList[].routeCode - Route identifier code
 * @param {string} routeList[].routeId - Unique route ID
 * @param {string} routeList[].type - Route type
 * @param {number} routeList[].routeSpeed - Route speed value
 * @param {Array} routeList[].picklists - Array of picklist objects within the route
 * @param {Object} routeList[].picklists[].associates - Associate information
 * @param {number} routeList[].picklists[].associates[].pickingDuration - Time spent picking in milliseconds
 * @param {string} routeList[].picklists[].associates[].alias - Associate identifier
 * @param {number} routeList[].picklists[].stageByTime - Stage completion time in milliseconds
 * @param {string} routeList[].picklists[].status - Current picklist status
 * @returns {Array<Array<string>>} 2D array where first row contains column headers and subsequent rows contain route data
 */
export function getRouteStatusDataInCSVFormat(routeList) {
  try {
    // Validate input parameter
    if (!Array.isArray(routeList)) {
      console.error(
        "Invalid routeList provided to getRouteStatusDataInCSVFormat",
      );
      return [];
    }

    const csvData = [];
    let columns = ROUTE_STATUS_EXPORT_CSV_TABLE_COLUMNS;

    // Add header row
    csvData.push(columns);

    // Process each route and its picklists
    routeList.forEach((route) => {
      if (!route || !route.picklists) return;

      // Create a row for each picklist within the route
      route.picklists.forEach((picklist, index) => {
        // Extract associate data (first associate if multiple exist)
        const associate =
          picklist && picklist.associates && picklist.associates[0];

        // Convert picking duration from milliseconds to HH:MM format
        const pickingDuration =
          associate && associate.pickingDuration
            ? convertMillisecondsToHHMM(associate.pickingDuration)
            : FALLBACK_ELLIPSIS;

        // Convert stage by time from milliseconds to HH:MM format
        const stageByTime =
          picklist && picklist.stageByTime
            ? convertMillisecondsToHHMM(picklist.stageByTime)
            : FALLBACK_ELLIPSIS;

        // Get formatted stage time and location using utility functions
        const stageTime = getPicklistStageTime(picklist);
        const stagingLocation = getPicklistLocation(picklist);
        const picklistStatus =
          picklist && picklist.status ? picklist.status : FALLBACK_DASH;

        // Build row data array matching column order
        const rowData = [
          (route && route.routeCode) || FALLBACK_DASH,
          stageByTime,
          (route && route.routeId) || FALLBACK_DASH,
          stagingLocation,
          picklistStatus,
          getPicklistProgress(picklist),
          (associate && associate.alias) || FALLBACK_DASH,
          stageTime,
          pickingDuration,
          (route && route.type) || "",
          renderRouteSpeed(route && route.routeSpeed),
        ];
        csvData.push(rowData);
      });
    });
    return csvData;
  } catch (error) {
    console.error("Error in getRouteStatusDataInCSVFormat:", error);
    return [];
  }
}



// WEBPACK FOOTER //
// ./src/components/RouteStatus/utils/ExportUtils.js